// src/pages/SizingCostCombinedDev.tsx
// DEVELOPMENT VERSION - Phase 0: Guided UX for 60-second first result
// When ready, swap this into the main route or replace SizingCostCombined.tsx

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Server, DollarSign, ChevronDown, FileDown, Loader2 } from 'lucide-react';
import { SizingSummaryDashboard } from '@/components/sizing/SizingSummaryDashboard';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import ErrorPanel from '@/components/outputs/ErrorPanel';
import { NarrativePanels } from '@/components/outputs/NarrativePanels';
import { CostSimulatorTool } from '@/components/cost-simulator/CostSimulatorTool';

// New Phase 0 components
import { QuickStartCard, type TeamType, type ResponseSpeed, type KnowledgeScope, getScopeForModelSize, TEAM_OPTIONS } from '@/components/sizing/QuickStartCard';
import { InstantResults } from '@/components/sizing/InstantResults';
// Full sidebar controls (RESTORED)
import { QuickControlsCollapsible } from '@/components/sizing/QuickControlsCollapsible';
import { RefinementPanel } from '@/components/sizing/RefinementPanel';
import { calculateConfidence } from '@/components/sizing/ConfidenceIndicator';

import { useCatalog } from '@/hooks/useCatalog';
import { usePersonaCatalog, usePersonaProfiles } from '@/hooks/usePersonaCatalog';
import { useSizingEngineV2 } from '@/hooks/useSizingEngineV2';
import { useCostSimulatorSizing } from '@/hooks/useCostSimulatorSizing';
import { useCostSimulatorState } from '@/hooks/useCostSimulatorState';
import { buildMcRequest } from '@/sizing/buildMcRequest';
import { useNewSizingNarrative } from '@/hooks/useNewSizingNarrative';
import type { NewSizingRequest } from '@/sizing/newSizingEngine';
import { SCENARIO_PRESETS } from '@/hooks/useSizingStateV2';
import { resolveEffectivePersona } from '@/lib/personaResolver';
import type { PersonaProfile } from '@/types/personas';
import { Button } from '@/components/ui/button';
import { generateTextBasedPdf, type PdfReportData } from '@/lib/pdfReportBuilder';
import { generateMonthlyRollups, findBreakEvenMonth, DEFAULT_COST_CONFIG } from '@/lib/costCalculations';
import { toast } from '@/hooks/use-toast';
import { usePersonaTemplates } from '@/hooks/usePersonaTemplates';
import type { UserType } from '@/types';

// Advanced panel (for refinement)
import AdvancedPanel from '@/components/inputs/AdvancedPanel';

const BYTES_PER_GIB = 1024 ** 3;

// Color palette for user personas
const USER_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28'];

const SPEED_TO_TOLERANCE: Record<ResponseSpeed, 'aggressive' | 'fast' | 'balanced' | 'relaxed' | 'queue'> = {
  fast: 'aggressive',
  balanced: 'balanced',
  relaxed: 'relaxed',
};

// Section Divider
const SectionDivider = ({
  title,
  icon: Icon,
  id
}: {
  title: string;
  icon: React.ElementType;
  id?: string;
}) => (
  <div id={id} className="py-8 border-y bg-muted/30 scroll-mt-16">
    <div className="container flex items-center gap-3 justify-center">
      <Icon className="h-6 w-6 text-primary" />
      <span className="text-xl font-semibold text-foreground">{title}</span>
    </div>
  </div>
);

const SizingCostCombinedDev = () => {
  // Refs for scrolling
  const sizingSectionRef = useRef<HTMLDivElement>(null);
  const costSectionRef = useRef<HTMLDivElement>(null);

  // QuickStart state (maps to existing sizing state)
  const [quickUserCount, setQuickUserCount] = useState(500);
  const [quickTeamType, setQuickTeamType] = useState<TeamType>('general-knowledge');
  const [quickSpeed, setQuickSpeed] = useState<ResponseSpeed>('balanced');
  const [quickScope, setQuickScope] = useState<KnowledgeScope>('broad');

  // Catalog and persona data
  const {
    catalog,
    isLoading: catalogLoading,
    error,
    catalogCommitted,
    catalogHash
  } = useCatalog();
  const catalogReady = catalog?.ready ?? false;
  const personaProfilesQuery = usePersonaProfiles();
  const personaProfiles = personaProfilesQuery.data ?? [];
  const sizing = useSizingEngineV2({
    personaProfiles: personaProfilesQuery.data ?? []
  });
  const personaCatalog = usePersonaCatalog({
    enabled: !catalogLoading && !error
  });
  const {
    snapshot,
    compute,
    loading
  } = sizing;

  // Fetch persona templates from database
  const { data: personaTemplates } = usePersonaTemplates();

  // Compute team composition for display
  const teamComposition = useMemo(() => {
    if (!personaTemplates || !personaProfiles.length) return [];
    
    const teamConfig = TEAM_OPTIONS.find(t => t.id === quickTeamType);
    if (!teamConfig) return [];
    
    const template = personaTemplates.find(t => t.id === teamConfig.templateId);
    if (!template?.items) return [];
    
    return template.items.map(item => {
      const profile = personaProfiles.find(p => p.id === item.persona_profile_id);
      return {
        name: profile?.display_name ?? item.persona_profile_id,
        percentage: item.percentage,
      };
    }).sort((a, b) => b.percentage - a.percentage);
  }, [quickTeamType, personaTemplates, personaProfiles]);

  // Sync Team Type selection to persona users
  useEffect(() => {
    if (!personaTemplates || !personaProfiles.length) return;
    
    const teamConfig = TEAM_OPTIONS.find(t => t.id === quickTeamType);
    if (!teamConfig) return;
    
    const template = personaTemplates.find(t => t.id === teamConfig.templateId);
    if (!template?.items) return;
    
    // Convert template items to UserType format
    const users: UserType[] = template.items.map((item, idx) => {
      const profile = personaProfiles.find(p => p.id === item.persona_profile_id);
      return {
        uid: item.persona_profile_id,
        id: item.persona_profile_id,
        profileId: item.persona_profile_id,
        label: profile?.display_name ?? item.persona_profile_id,
        pct: item.percentage,
        inTok: profile?.prompt_tokens_per_turn ?? 0,
        outTok: profile?.response_tokens_per_turn ?? 0,
        color: USER_COLORS[idx % USER_COLORS.length],
      };
    });
    
    sizing.setUsers(users);
  }, [quickTeamType, personaTemplates, personaProfiles, sizing.setUsers]);

  // Sync QuickStart to sizing state
  useEffect(() => {
    // Update concurrent users
    if (quickUserCount !== sizing.scenario.tradeoffs.concurrentUsers) {
      sizing.setTradeoff('concurrentUsers', quickUserCount);
    }
  }, [quickUserCount, sizing.scenario.tradeoffs.concurrentUsers, sizing.setTradeoff]);

  useEffect(() => {
    // Update latency tolerance based on speed selection
    const newTolerance = SPEED_TO_TOLERANCE[quickSpeed];
    if (newTolerance !== sizing.scenario.tradeoffs.latencyTolerance) {
      sizing.setTradeoff('latencyTolerance', newTolerance);
    }
  }, [quickSpeed, sizing.scenario.tradeoffs.latencyTolerance, sizing.setTradeoff]);

  // Sync Knowledge Scope to model selection
  useEffect(() => {
    const scopeToModelSize: Record<KnowledgeScope, number> = {
      focused: 7,
      broad: 70,
      expert: 405,
    };
    const targetSize = scopeToModelSize[quickScope];
    
    if (catalog?.models && catalog.models.length > 0) {
      // Find best matching model from catalog
      let bestMatch = catalog.models[0];
      let smallestDiff = Math.abs((bestMatch.params_b ?? 0) - targetSize);
      
      for (const model of catalog.models) {
        const diff = Math.abs((model.params_b ?? 0) - targetSize);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          bestMatch = model;
        }
      }
      
      if (bestMatch && bestMatch.model_id !== sizing.scenario.selectedModelId) {
        sizing.setSelectedModelId(bestMatch.model_id);
      }
    }
  }, [quickScope, catalog?.models, sizing.scenario.selectedModelId, sizing.setSelectedModelId]);

  // NOTE: Removed bidirectional sync (model → quickScope) to prevent circular update loops.
  // The user's Knowledge Scope selection is the source of truth; model follows scope.

  // Cost simulator state
  const costSizing = useCostSimulatorSizing();
  const costSnapshot = costSizing.snapshot;
  const { comparison } = useCostSimulatorState(costSnapshot);

  // PDF export state
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Helper functions
  const toNum = (value: unknown): number | null => {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const parseJson = <T,>(value: unknown): T | null => {
    if (!value) return null;
    try {
      return typeof value === 'string' ? JSON.parse(value) as T : value as T;
    } catch {
      return null;
    }
  };
  const resolvePersonaLatency = (profile: PersonaProfile | undefined, mode: 'snappy' | 'fast' | 'resp' | 'comfy' | 'async' | 'responsive') => {
    if (!profile) {
      return { ttftSec: 0, decodeTokPerSec: 0 };
    }
    const key = mode === 'responsive' ? 'resp' : mode;
    const ttftMsByKey: Record<'snappy' | 'fast' | 'resp' | 'comfy' | 'async', number | null> = {
      snappy: toNum((profile as any).ttft_snappy_ms),
      fast: toNum((profile as any).ttft_fast_ms),
      resp: toNum((profile as any).ttft_resp_ms),
      comfy: toNum((profile as any).ttft_comfy_ms),
      async: toNum((profile as any).ttft_async_ms)
    };
    const decodeTpsByKey: Record<'snappy' | 'fast' | 'resp' | 'comfy' | 'async', number | null> = {
      snappy: toNum((profile as any).decode_tps_snappy),
      fast: toNum((profile as any).decode_tps_fast),
      resp: toNum((profile as any).decode_tps_resp),
      comfy: toNum((profile as any).decode_tps_comfy),
      async: toNum((profile as any).decode_tps_async)
    };
    const ttftMs = ttftMsByKey[key] ?? toNum(profile.default_latency_slo_ms) ?? 0;
    const decodeTokPerSec = decodeTpsByKey[key] ?? 0;
    const ttftSec = ttftMs / 1000;
    return { ttftSec, decodeTokPerSec };
  };

  // Latency mode derivation
  const tolerance = sizing.scenario.tradeoffs.latencyTolerance ?? 'balanced';
  const slaMode = (() => {
    switch (tolerance) {
      case 'aggressive': return 'snappy' as const;
      case 'fast': return 'fast' as const;
      case 'balanced': return 'responsive' as const;
      case 'relaxed': return 'comfy' as const;
      case 'queue':
      case 'none':
      default: return 'async' as const;
    }
  })();
  const defaultBatchForTolerance: Record<string, number> = {
    aggressive: 1,
    fast: 4,
    balanced: 16,
    relaxed: 64,
    queue: 256,
    none: 256
  };
  const effectiveBatch = defaultBatchForTolerance[tolerance] ?? defaultBatchForTolerance.balanced;
  const mcLatencyMode = slaMode === 'responsive' ? 'resp' : slaMode;

  // Throughput entry
  const throughputEntry = useMemo(() => {
    if (!catalogCommitted || !catalogReady) return null;
    if (!catalog?.throughput?.length) return null;
    const modelId = sizing.scenario.selectedModelId ?? catalog.models?.[0]?.model_id;
    if (!modelId) return null;
    const precisionBits = Number(sizing.scenario.tradeoffs.precision ?? 8);
    const backend = catalog.accelerator?.backend ?? null;
    const batchTarget = effectiveBatch;
    const throughputPoints = catalog.throughput;
    const primaryCandidates = throughputPoints.filter(point => {
      if (point.model_id !== modelId) return false;
      if (Number(point.precision) !== precisionBits) return false;
      if (backend && (point.backend ?? backend) !== backend) return false;
      return true;
    });
    let chosen = null as (typeof throughputPoints)[number] | null;
    if (primaryCandidates.length > 0) {
      const sorted = [...primaryCandidates].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
      chosen = sorted.find(p => (p.batch ?? 0) >= batchTarget) ?? sorted[sorted.length - 1];
    }
    if (!chosen) {
      const modelBackendCandidates = throughputPoints.filter(point => {
        if (point.model_id !== modelId) return false;
        if (backend && (point.backend ?? backend) !== backend) return false;
        return true;
      });
      if (modelBackendCandidates.length > 0) {
        const sorted = [...modelBackendCandidates].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
        chosen = sorted.find(p => (p.batch ?? 0) >= batchTarget) ?? sorted[sorted.length - 1];
      }
    }
    if (!chosen) {
      const anyModel = throughputPoints.filter(point => point.model_id === modelId);
      if (anyModel.length > 0) {
        const sorted = [...anyModel].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
        chosen = sorted.find(p => (p.batch ?? 0) >= batchTarget) ?? sorted[sorted.length - 1];
      }
    }
    chosen = chosen ?? throughputPoints[0] ?? null;
    return chosen;
  }, [catalog, catalogCommitted, catalogReady, sizing.scenario.selectedModelId, effectiveBatch, sizing.scenario.tradeoffs.precision]);

  // Build sizing request
  const usersCount = sizing.scenario.users.length;
  const personaCatalogReady = !personaCatalog.isLoading && !!personaCatalog.data;
  const profilesReady = !personaProfilesQuery.isLoading && !!personaProfilesQuery.data;
  const sizingRequest = useMemo<NewSizingRequest | null>(() => {
    if (catalogLoading) return null;
    if (!catalogReady || !catalogCommitted) return null;
    if (!catalog || !personaCatalogReady || !throughputEntry || !profilesReady) return null;
    const concurrentUsers = sizing.scenario.tradeoffs.concurrentUsers ?? 0;
    if (concurrentUsers <= 0) return null;
    if (usersCount <= 0) return null;
    const profilesById: Record<string, PersonaProfile> = Object.fromEntries(personaProfiles.map(p => [p.id, p]));
    const scenarioUsers = sizing.scenario.users;
    const personaCount = scenarioUsers.length || 1;
    const turns = sizing.scenario.tradeoffs.turns ?? 1;
    const effectivePersonas = scenarioUsers.map(user => resolveEffectivePersona(user, profilesById, slaMode, personaCount));
    const personas = effectivePersonas.map((persona, idx) => {
      const user = scenarioUsers[idx];
      const profile = user?.profileId && user.profileId !== 'custom' ? profilesById[user.profileId] : undefined;
      const population = concurrentUsers * (persona.sharePercent / 100);
      const personaLatencyMode = persona.latencyMode ?? slaMode;
      const latencyKey = personaLatencyMode === 'responsive' ? 'resp' : personaLatencyMode;
      const effectiveLatencyMs = profile ? (() => {
        switch (latencyKey) {
          case 'snappy': return toNum((profile as any).e2e_target_snappy_ms);
          case 'fast': return toNum((profile as any).e2e_target_fast_ms);
          case 'resp': return toNum((profile as any).e2e_target_resp_ms);
          case 'comfy': return toNum((profile as any).e2e_target_comfy_ms);
          case 'async': return toNum((profile as any).e2e_target_async_ms);
          default: return null;
        }
      })() : null;
      const inputTokensPerConversation = persona.inputTokensPerTurn * turns;
      const outputTokensPerConversation = persona.outputTokensPerTurn * turns;
      const { ttftSec, decodeTokPerSec } = resolvePersonaLatency(profile, latencyKey);
      const streamSec = decodeTokPerSec > 0 ? outputTokensPerConversation / decodeTokPerSec : 0;
      const digestSec = ttftSec + streamSec;
      const latency_profiles = profile ? {
        snappy: { ttft_ms: toNum((profile as any).ttft_snappy_ms) ?? 0, decode_tps: toNum((profile as any).decode_tps_snappy) ?? 0 },
        fast: { ttft_ms: toNum((profile as any).ttft_fast_ms) ?? 0, decode_tps: toNum((profile as any).decode_tps_fast) ?? 0 },
        resp: { ttft_ms: toNum((profile as any).ttft_resp_ms) ?? 0, decode_tps: toNum((profile as any).decode_tps_resp) ?? 0 },
        comfy: { ttft_ms: toNum((profile as any).ttft_comfy_ms) ?? 0, decode_tps: toNum((profile as any).decode_tps_comfy) ?? 0 },
        async: { ttft_ms: toNum((profile as any).ttft_async_ms) ?? 0, decode_tps: toNum((profile as any).decode_tps_async) ?? 0 }
      } : undefined;
      return {
        id: persona.id ?? `persona-${idx}`,
        name: persona.display_name ?? `Persona ${idx + 1}`,
        display_name: persona.display_name ?? `Persona ${idx + 1}`,
        prompt_tokens_per_turn: persona.inputTokensPerTurn,
        response_tokens_per_turn: persona.outputTokensPerTurn,
        turns_kept: turns,
        read_sec_default: persona.readSec,
        think_sec_default: persona.thinkSec,
        sharePercent: persona.sharePercent,
        population,
        inputTokensPerConversation,
        outputTokensPerConversation,
        readSeconds: persona.readSec,
        thinkSeconds: persona.thinkSec,
        ttftMs: ttftSec * 1000,
        decodeTps: decodeTokPerSec,
        turnsKept: turns,
        latencyTargetMs: effectiveLatencyMs,
        tokens: { input: persona.inputTokensPerTurn, output: persona.outputTokensPerTurn },
        readSec: persona.readSec,
        thinkSec: persona.thinkSec,
        digestSec,
        turnsPerSession: turns,
        latency_profiles,
        latency_slo_default_ms: profile?.default_latency_slo_ms ?? 0,
        profileId: user?.profileId
      };
    });
    const selectedModelId = sizing.scenario.selectedModelId ?? catalog.models?.[0]?.model_id ?? 'model';
    const modelRow = catalog.models?.find(m => m.model_id === selectedModelId) ?? catalog.models?.find(m => m.model_id === catalog.models?.[0]?.model_id);
    const precisionBits = Number(sizing.scenario.tradeoffs.precision ?? 8);
    let kvBytesPerToken = 0;
    let weightsGiB = 0;
    let weightsGiBUnshared = 0;
    let runtimeOverheadGiB = 0;
    let usableKvGiBPerGpu = 0;
    let hbmGiB = 0;
    let fitsWeightsWithoutSharing = false;
    let fitsWeightsWithSharing = false;
    let bridgeGroupSize = 1;
    let weightsSharedWithinGroup = false;
    if (modelRow && catalog.accelerator) {
      const kvMap = parseJson<Record<string, number>>(modelRow.kv_per_token_json) ?? {};
      const weightsMap = parseJson<Record<string, number>>(modelRow.weights_mem_json) ?? {};
      kvBytesPerToken = kvMap[String(precisionBits)] ?? 0;
      const accel = catalog.accelerator as any;
      bridgeGroupSize = Number(accel.bridge_group_size_max ?? 1) || 1;
      weightsSharedWithinGroup = true;
      const weightsBytesRaw = weightsMap[String(precisionBits)] ?? 0;
      weightsGiBUnshared = weightsBytesRaw / BYTES_PER_GIB;
      const weightsBytesPerGpu = weightsSharedWithinGroup && bridgeGroupSize > 0 ? weightsBytesRaw / bridgeGroupSize : weightsBytesRaw;
      weightsGiB = weightsBytesPerGpu / BYTES_PER_GIB;
      const hbmB = Number(accel.hbm_bytes ?? 0);
      hbmGiB = hbmB / BYTES_PER_GIB;
      const modelRuntimeBytes = Number(modelRow.base_runtime_overhead_bytes ?? 0);
      const reservedRuntimeBytesPerGpu = Number(accel.reserved_runtime_bytes ?? 0);
      const runtimeGroupBytes = Number(accel.runtime_overhead_bytes_per_group ?? 0);
      const runtimeGroupBytesPerGpu = bridgeGroupSize > 0 ? runtimeGroupBytes / bridgeGroupSize : runtimeGroupBytes;
      const totalRuntimeBytesPerGpu = modelRuntimeBytes + reservedRuntimeBytesPerGpu + runtimeGroupBytesPerGpu;
      runtimeOverheadGiB = totalRuntimeBytesPerGpu / BYTES_PER_GIB;
      fitsWeightsWithoutSharing = hbmGiB > 0 ? weightsGiBUnshared + runtimeOverheadGiB <= hbmGiB : false;
      fitsWeightsWithSharing = hbmGiB > 0 ? weightsGiB + runtimeOverheadGiB <= hbmGiB : false;
      usableKvGiBPerGpu = Math.max(0, hbmGiB - weightsGiB - runtimeOverheadGiB);
    }
    const model = {
      id: modelRow?.model_id ?? selectedModelId,
      paramsBillions: Number(modelRow?.params_b ?? 0),
      precisionBits,
      kvBytesPerToken,
      weightsGiB,
      usableKvGiBPerGpu,
      hbmGiB,
      weightsGiBUnshared,
      fitsWeightsWithoutSharing,
      fitsWeightsWithSharing,
      bridgeGroupSize,
      weightsSharedWithinGroup
    };
    const throughput = {
      promptTpsPerGpu: Number((throughputEntry as any).prompt_tps ?? (throughputEntry as any).promptTps ?? 0),
      decodeTpsPerGpu: Number((throughputEntry as any).decode_tps ?? (throughputEntry as any).decodeTps ?? 0)
    };
    const uiState = {
      conversationTurns: sizing.scenario.tradeoffs.turns ?? 3,
      batchSize: effectiveBatch,
      latencySloMs: sizing.scenario.advanced.latencyMs ?? sizing.scenario.advanced.latencyTargetMs ?? 0,
      mcFastTrials: 8,
      mcRefineTrials: 32,
      mcHorizonSeconds: 600,
      trafficVolatility: 0.2
    };
    const req = buildMcRequest({
      personas,
      model,
      throughput,
      uiState,
      scenario: { concurrentUsers },
      latencyMode: mcLatencyMode
    });
    if (!req) return null;
    return req;
  }, [catalog, catalogHash, catalogCommitted, catalogLoading, catalogReady, personaCatalog.data, personaCatalogReady, throughputEntry, personaProfiles, profilesReady, sizing.scenario.selectedModelId, sizing.scenario.tradeoffs.concurrentUsers, sizing.scenario.tradeoffs.turns, sizing.scenario.tradeoffs.latencyTolerance, sizing.scenario.advanced.latencyMs, sizing.scenario.advanced.latencyTargetMs, sizing.scenario.users, usersCount, effectiveBatch, slaMode, mcLatencyMode]);

  // Trigger sizing computation
  useEffect(() => {
    if (!sizingRequest) return;
    compute(sizingRequest);
  }, [compute, sizingRequest]);

  // Narrative data for PDF
  const narrativeData = useNewSizingNarrative({
    snapshot,
    modelName: sizingRequest?.model.id,
    serverSku: 'XE7740',
    serverDisplayName: 'Dell XE7740',
    gpusPerServer: 8,
    hbmBytesPerGpu: (sizingRequest?.model.hbmGiB ?? 96) * BYTES_PER_GIB,
    weightsGiB: sizingRequest?.model.weightsGiB,
    weightsGiBUnshared: sizingRequest?.model.weightsGiBUnshared ?? sizingRequest?.model.weightsGiB,
    runtimeOverheadGiB: sizingRequest ? (sizingRequest.model.hbmGiB ?? 96) - sizingRequest.model.weightsGiB - sizingRequest.model.usableKvGiBPerGpu : undefined,
    usableKvGiBPerGpu: sizingRequest?.model.usableKvGiBPerGpu,
    bridgeGroupSize: sizingRequest?.model.bridgeGroupSize ?? 1,
    conversationTurns: sizingRequest?.conversationTurns,
    latencyMs: sizingRequest?.latencySloMs,
    batchSize: sizingRequest?.batchSize,
    baseConcurrentUsers: sizingRequest?.baseConcurrentUsers,
    personas: sizingRequest?.personas
  });

  // Scroll handlers
  const scrollToSizing = useCallback(() => {
    sizingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const scrollToCost = useCallback(() => {
    costSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // PDF Export handler
  const handleExportPdf = useCallback(async () => {
    if (!snapshot || !sizingRequest) {
      toast({ title: 'Unable to Export', description: 'Please wait for sizing results to load.', variant: 'destructive' });
      return;
    }
    setIsExportingPdf(true);
    try {
      const costConfig = { ...DEFAULT_COST_CONFIG };
      const monthlyRollups = generateMonthlyRollups(snapshot, costConfig);
      const costComparison = findBreakEvenMonth(monthlyRollups);
      const pdfData: PdfReportData = {
        snapshot,
        request: sizingRequest,
        narrativeData,
        serverDisplayName: catalog?.server?.display_name ?? 'Dell XE7740',
        acceleratorDisplayName: catalog?.accelerator?.display_name ?? 'Intel Gaudi 3',
        costData: { config: costConfig, comparison: costComparison, monthlyRollups },
      };
      await generateTextBasedPdf(pdfData);
      toast({ title: 'Report Exported', description: 'Your sizing report has been downloaded as PDF.' });
    } catch (err) {
      console.error('[SizingCostCombinedDev] PDF export error:', err);
      toast({ title: 'Export Failed', description: 'Unable to generate PDF. Please try again.', variant: 'destructive' });
    } finally {
      setIsExportingPdf(false);
    }
  }, [snapshot, sizingRequest, narrativeData, catalog]);

  // Calculate confidence based on current inputs
  const confidenceInputs = useMemo(() => {
    const hasCustomPersonas = sizing.scenario.users.some(u => u.profileId !== 'custom');
    const hasModelSelected = !!sizing.scenario.selectedModelId;
    const hasPrecisionTuned = sizing.scenario.tradeoffs.precision !== 8;
    return {
      hasBasicInputs: true, // Always true after quick start
      hasCustomPersonas,
      hasModelSelected,
      hasPrecisionTuned,
    };
  }, [sizing.scenario.users, sizing.scenario.selectedModelId, sizing.scenario.tradeoffs.precision]);

  const confidenceResult = useMemo(() => {
    return calculateConfidence(confidenceInputs);
  }, [confidenceInputs]);

  // Derived values for results
  const serverCount = snapshot ? Math.ceil(snapshot.finalGpuCount / 8) : 0;
  const gpuCount = snapshot?.finalGpuCount ?? 0;

  // Loading state
  if (catalogLoading || personaCatalog.isLoading || personaProfilesQuery.isLoading) {
    return <LoadingScreen message="Loading catalog..." />;
  }

  // Error state
  if (error || personaCatalog.error || personaProfilesQuery.error) {
    const err = error as Error ?? personaCatalog.error as Error ?? personaProfilesQuery.error as Error;
    return <ErrorPanel title="Catalog Load Failed" message="Unable to load sizing catalog. Please try again." error={err} />;
  }

  // Model display name for cost simulator
  const modelDisplayName = costSnapshot?.modelId ?? sizingRequest?.model.id;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section: Guided Walkthrough */}
      <section className="py-8 border-b bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Compact Header */}
            <div className="text-center">
              <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Plan Your GenAI Infrastructure
              </h1>
              <p className="text-muted-foreground text-lg">
                Get a sizing estimate in 60 seconds, then refine below
              </p>
            </div>

            {/* Quick Start Card - 4 Questions */}
            <QuickStartCard
              userCount={quickUserCount}
              teamType={quickTeamType}
              responseSpeed={quickSpeed}
              knowledgeScope={quickScope}
              onUserCountChange={setQuickUserCount}
              onTeamTypeChange={setQuickTeamType}
              onSpeedChange={setQuickSpeed}
              onKnowledgeScopeChange={setQuickScope}
              teamComposition={teamComposition}
            />

            {/* Instant Results */}
            <InstantResults
              serverCount={serverCount}
              gpuCount={gpuCount}
              breakEvenMonths={comparison.breakEvenMonthIndex}
              confidenceLevel={confidenceResult.level}
              confidencePercentage={confidenceResult.percentage}
              confidenceNextSteps={confidenceResult.nextSteps}
              isLoading={loading}
              hasModelSelected={confidenceInputs.hasModelSelected}
              hasCustomPersonas={confidenceInputs.hasCustomPersonas}
              hasPrecisionTuned={confidenceInputs.hasPrecisionTuned}
              // Context for explanation generation
              teamType={quickTeamType}
              responseSpeed={quickSpeed}
              concurrentUsers={quickUserCount}
              chatMemoryTurns={sizing.scenario.tradeoffs.turns}
              modelParamsB={sizingRequest?.model?.paramsBillions}
            />
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <SectionDivider id="sizing-section" title="Infrastructure Sizing" icon={Server} />

      {/* Main Sizing Section with Sidebar + Results */}
      <section ref={sizingSectionRef} className="py-8 scroll-mt-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* 2-Column Grid: Sidebar (1/3) + Results (2/3) */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 items-start">
              {/* Sidebar with Full Controls - RESTORED */}
              <div className="lg:col-span-1">
                <div className="sticky top-20 self-start">
                  <QuickControlsCollapsible
                    tradeoffs={sizing.scenario.tradeoffs}
                    users={sizing.scenario.users}
                    totalPct={sizing.scenario.totalPct}
                    warningUnderPct={sizing.scenario.warningUnderPct}
                    blockedOverPct={sizing.scenario.blockedOverPct}
                    catalog={catalog}
                    catalogLoading={catalogLoading}
                    selectedModelId={sizing.scenario.selectedModelId}
                    onModelChange={sizing.setSelectedModelId}
                    onTradeoffChange={sizing.setTradeoff}
                    onUserUpdate={sizing.updateUser}
                    onUserRemove={sizing.removeUser}
                    onUserAdd={sizing.addUser}
                    advanced={sizing.scenario.advanced}
                    onAdvancedChange={sizing.setAdvanced}
                    sticky={false}
                    presets={SCENARIO_PRESETS}
                    onPresetSelect={sizing.applyPreset}
                  />
                </div>
              </div>

              {/* Results Panel - 2/3 Width */}
              <div className="lg:col-span-2 space-y-6">
                <SizingSummaryDashboard
                  snapshot={snapshot}
                  serverCount={serverCount}
                  gpusPerServer={8}
                  serverDisplayName={catalog?.server?.display_name ?? 'XE7740 Servers'}
                  acceleratorDisplayName={catalog?.accelerator?.display_name ?? 'Intel Gaudi 3 Accelerators'}
                />

                {/* PDF Export */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleExportPdf}
                    disabled={!snapshot || !sizingRequest || isExportingPdf}
                    className="gap-2"
                    variant="outline"
                  >
                    {isExportingPdf ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        <FileDown className="h-4 w-4" />
                        Export PDF Report
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Full-width Advanced Insights (below the grid) */}
            {snapshot && sizingRequest && (
              <div className="mt-8">
                <NarrativePanels
                  snapshot={snapshot}
                  modelName={sizingRequest.model.id}
                  serverSku="XE7740"
                  serverDisplayName="Dell XE7740"
                  gpusPerServer={8}
                  hbmBytesPerGpu={(sizingRequest.model.hbmGiB ?? 96) * BYTES_PER_GIB}
                  weightsGiB={sizingRequest.model.weightsGiB}
                  weightsGiBUnshared={sizingRequest.model.weightsGiBUnshared ?? sizingRequest.model.weightsGiB}
                  runtimeOverheadGiB={(sizingRequest.model.hbmGiB ?? 96) - sizingRequest.model.weightsGiB - sizingRequest.model.usableKvGiBPerGpu}
                  usableKvGiBPerGpu={sizingRequest.model.usableKvGiBPerGpu}
                  bridgeGroupSize={sizingRequest.model.bridgeGroupSize ?? 1}
                  conversationTurns={sizingRequest.conversationTurns}
                  latencyMs={sizingRequest.latencySloMs}
                  batchSize={sizingRequest.batchSize}
                  baseConcurrentUsers={sizingRequest.baseConcurrentUsers}
                  personas={sizingRequest.personas}
                  isComputing={loading}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cost Simulator Section Divider */}
      <SectionDivider id="cost-section" title="Cost Analysis" icon={DollarSign} />

      {/* Cost Simulator Section */}
      <section ref={costSectionRef} className="py-8 pb-16 scroll-mt-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <CostSimulatorTool snapshot={costSnapshot} modelDisplayName={modelDisplayName} sizing={costSizing} />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/30 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            Powered by <span className="font-semibold text-foreground">VectorPath Consulting</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SizingCostCombinedDev;
